import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdminLinkAction,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  CONTACT_EMAIL_DEFAULTS,
  DOMAINS_DEFAULTS,
  NOTIFICATION_METHOD_DEFAULTS,
  PLATFORM_IDENTITY_DEFAULTS,
} from './settingsDefaults';
import {
  SettingsConfirmModal,
  SettingsField,
  SettingsReadOnlyRow,
  SettingsRelatedLink,
  SettingsSectionShell,
  SettingsSuccessToast,
  useSettingsPanelState,
} from './SettingsSectionShell';

const SUBSECTIONS = [
  { id: 'delivery', label: 'Global Delivery Methods' },
  { id: 'sender', label: 'Sender Identity' },
  { id: 'push-branding', label: 'Browser Push Branding' },
  { id: 'related', label: 'Related Notification Controls' },
];

const AdminNotificationsPanel = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('delivery');
  const [confirm, setConfirm] = useState(null);

  const delivery = useSettingsPanelState({ ...NOTIFICATION_METHOD_DEFAULTS });

  const panels = {
    delivery,
    sender: { hasChanges: false, saving: false, cancel: () => {}, successMessage: '' },
    'push-branding': { hasChanges: false, saving: false, cancel: () => {}, successMessage: '' },
    related: { hasChanges: false, saving: false, cancel: () => {}, successMessage: '' },
  };
  const activePanel = panels[activeId];

  const requestSave = () => {
    if (activeId !== 'delivery') return;

    const emailChanged = delivery.draft.email !== delivery.saved.email;
    const pushChanged = delivery.draft.browserPush !== delivery.saved.browserPush;
    const changingMethod = emailChanged
      ? { name: 'Email', next: delivery.draft.email }
      : pushChanged
        ? { name: 'Browser Push', next: delivery.draft.browserPush }
        : null;

    if (changingMethod && changingMethod.next === 'Paused') {
      setConfirm({
        title: 'Pause Global Delivery Method?',
        message: `Due notifications using ${changingMethod.name} will be skipped while it is unavailable.`,
      });
      return;
    }
    if (changingMethod && changingMethod.next === 'Off') {
      setConfirm({
        title: 'Turn Off Global Delivery Method?',
        message: `${changingMethod.name} will remain unavailable until manually enabled; due deliveries will be skipped.`,
      });
      return;
    }
    if (changingMethod && changingMethod.next === 'On') {
      setConfirm({
        title: 'Enable Global Delivery Method?',
        message: `${changingMethod.name} will become available to applicable Notification Rules. Previously skipped deliveries are not replayed.`,
      });
      return;
    }
    performSave();
  };

  const performSave = async () => {
    setConfirm(null);
    await delivery.save(() => ({}), 'Global notification methods updated.');
  };

  const MethodRadios = ({ label, field, locked }) => (
    <SettingsField label={label} required={!locked}>
      {locked ? (
        <label className="flex items-center gap-2 text-sm text-mediumgray">
          <input type="radio" checked disabled className="w-4 h-4 text-primaryblue border-lightgray" />
          On — locked
        </label>
      ) : (
        <div className="flex flex-wrap gap-4 text-sm text-offblack">
          {['On', 'Paused', 'Off'].map((value) => (
            <label key={value} className="flex items-center gap-2">
              <input
                type="radio"
                checked={delivery.draft[field] === value}
                onChange={() => delivery.setField(field, value)}
                className="w-4 h-4 text-primaryblue border-lightgray"
              />
              {value}
            </label>
          ))}
        </div>
      )}
    </SettingsField>
  );

  const body = {
    delivery: (
      <>
        <MethodRadios label="Dashboard" field="dashboard" locked />
        <MethodRadios label="Email" field="email" />
        <MethodRadios label="Browser Push" field="browserPush" />
      </>
    ),
    sender: (
      <>
        <div className={adminFormTwoColGridClass}>
          <SettingsReadOnlyRow
            label="Notification From Name"
            value={CONTACT_EMAIL_DEFAULTS.notificationFromName}
          />
          <SettingsReadOnlyRow
            label="Notification From Email"
            value={CONTACT_EMAIL_DEFAULTS.notificationFromEmail}
          />
          <SettingsReadOnlyRow
            label="Notification Reply-To Email"
            value={CONTACT_EMAIL_DEFAULTS.notificationReplyToEmail}
          />
          <SettingsReadOnlyRow
            label="Sending Domain"
            value={DOMAINS_DEFAULTS.notificationSendingDomain}
          />
          <SettingsReadOnlyRow
            label="Provider Verification Status"
            value="Retrieved from Integrations"
          />
        </div>
        <div className="space-y-2">
          <AdminLinkAction
            type="button"
            allowWhenRestricted
            onClick={() => navigate('/admin/settings/general')}
          >
            Edit Sender Identity
          </AdminLinkAction>
          <SettingsRelatedLink onClick={() => navigate('/admin/settings/general')}>
            Domains & URLs
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/integrations')}>
            Open Email Integration
          </SettingsRelatedLink>
        </div>
      </>
    ),
    'push-branding': (
      <>
        <div className={adminFormTwoColGridClass}>
          <SettingsReadOnlyRow
            label="Push Display Name"
            value={PLATFORM_IDENTITY_DEFAULTS.platformDisplayName}
          />
          <SettingsReadOnlyRow label="Push Icon" value="Compact Brand Icon" />
          <SettingsReadOnlyRow
            label="Global Browser Push State"
            value={delivery.saved.browserPush}
          />
        </div>
      </>
    ),
    related: (
      <>
        <div className="space-y-2">
          <SettingsRelatedLink onClick={() => navigate('/admin/configuration/notifications')}>
            Open Notification Rules
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/system-logs?tab=notifications')}>
            Open Notification Logs
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/integrations')}>
            Open Email Integration
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/integrations')}>
            Open Browser Push Integration
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/integrations')}>
            Open Integrations
          </SettingsRelatedLink>
        </div>
      </>
    ),
  }[activeId];

  return (
    <>
      <SettingsSectionShell
        subsections={SUBSECTIONS}
        activeId={activeId}
        onSelectSubsection={setActiveId}
        hasChanges={Boolean(activePanel.hasChanges)}
        saving={Boolean(activePanel.saving)}
        onSave={requestSave}
        onCancel={activePanel.cancel || (() => {})}
        showFooter={activeId === 'delivery'}
      >
        {body}
      </SettingsSectionShell>

      {confirm ? (
        <SettingsConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={performSave}
          confirming={delivery.saving}
        />
      ) : null}

      <SettingsSuccessToast message={delivery.successMessage} />
    </>
  );
};

export default AdminNotificationsPanel;
